import React, { useState } from 'react'
import { ChevronDown } from 'lucide-react'

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null)

  const faqs = [
    {
      question: 'How does app locking actually work? Can I bypass it?',
      answer:
        'ZoeLive Prayer uses iOS Screen Time API (iOS 16+) and Android AccessibilityService (Android 8+) to genuinely block apps — not just remind you. Once Prayer Mode starts, locked apps won\'t open until the timer ends. The only way to end early is holding the "End Prayer" button for 3 seconds, which gives you time to reconsider. You can disable the service in your phone\'s settings, but that defeats the purpose! 85% of users complete sessions without ending early.',
    },
    {
      question: 'Is ZoeLive Prayer really free? What\'s the catch?',
      answer:
        'Yes, 100% free forever for core features: unlimited Prayer Mode, streak tracking, all 600+ devotions (streaming), and joining 1 Prayer Pod. No ads. No spam. Premium (R99/month) unlocks analytics, unlimited Pods, and advanced features — but you can pray consistently without ever paying. We\'re believers serving believers, not a bait-and-switch freemium trap.',
    },
    {
      question: 'What if I miss a day and break my streak?',
      answer:
        'Streaks require 5 minutes or more of prayer daily. We include a grace period until 3am the next day (late-night prayers count!). If you break your streak, you can start fresh immediately. Premium users get 1 "Streak Freeze" per month — use it during holidays or sick days to preserve your streak. Your longest-ever streak is always saved in your profile to celebrate past wins.',
    },
    {
      question: 'Can I use ZoeLive Prayer without joining a Pod?',
      answer:
        'Absolutely! Pods are optional. Many users prefer solo prayer with streak tracking. That said, our data shows Pod members pray 2.3x more consistently and hit 30-day streaks at twice the rate. You can join a public Pod (browse by topic like "20s Believers" or "Parents") or create a private one for your small group.',
    },
    {
      question: 'What denominations are the devotions for?',
      answer:
        'Our 600+ devotions span evangelical, Catholic, mainline Protestant, charismatic, and non-denominational perspectives. We label each devotion by theological tradition so you can filter what resonates. Topics include anxiety, gratitude, forgiveness, healing, worship, and more. All content is reviewed by our Faith Advisor Council to ensure biblical accuracy without being preachy or exclusive.',
    },
    {
      question: 'Does Premium auto-renew? Can I cancel easily?',
      answer:
        'Yes, Premium auto-renews monthly (R99) or annually (R990). You can cancel anytime in your phone\'s subscription settings (iOS: Settings > Subscriptions, Android: Play Store > Subscriptions). No sneaky cancellation processes. No "are you sure?" guilt trips. We follow App Store and Google Play standard policies. 7-day free trial means you\'re not charged until Day 8.',
    },
    {
      question: 'Will this work offline (aeroplane mode, no WiFi)?',
      answer:
        'Yes! Download devotions for offline playback (Free: 10, Premium: 50). Prayer Mode and streak tracking work without internet. Analytics and Pod leaderboards sync when you reconnect. Perfect for flights, camping, or anywhere with spotty service.',
    },
  ]

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section id="faq" className="section-padding bg-white">
      <div className="container-custom">
        {/* Headline */}
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-heading-dark mb-4">
            Questions? We've Got <span className="text-gradient">Answers</span>.
          </h2>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="card animate-on-scroll"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between text-left gap-4"
              >
                <h3 className="text-lg font-semibold text-heading-dark pr-4">
                  {faq.question}
                </h3>
                <ChevronDown
                  size={24}
                  className={`flex-shrink-0 text-prayer-blue transition-transform duration-300 ${
                    openIndex === index ? 'transform rotate-180' : ''
                  }`}
                />
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 mt-4' : 'max-h-0'
                }`}
              >
                <p className="text-body-gray leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Contact */}
        <div className="text-center mt-12 animate-on-scroll">
          <p className="text-body-gray mb-2">Still have questions?</p>
          <a
            href="mailto:hello@zoeliveprayer.co.za"
            className="text-prayer-blue font-semibold hover:underline"
          >
            Email us at hello@zoeliveprayer.co.za
          </a>
          <p className="text-sm text-muted-gray mt-2">
            We respond within 24 hours (Premium users get priority support)
          </p>
        </div>
      </div>
    </section>
  )
}

export default FAQ
